import { useToast } from '@pancakeswap/uikit'
import { useUserSlippage } from '@pancakeswap/utils/user'
import { useCallback, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import { usePublicClient } from 'wagmi'
import { getWalletClient } from 'wagmi/actions'
import type { Address, Hex } from 'viem'
import useTransactionDeadline from 'hooks/useTransactionDeadline'
import { V3_CHAIN_ID } from 'views/TradingCompetitionV3/config'
import { useV3Wallet } from 'views/TradingCompetitionV3/hooks'
import { liveGasParams } from 'utils/liveGas'
import { erc20Abi } from './abi'
import { Account, Impact, PoolError, PoolView, impactOf, makeResolver, quoteTrade, readAccount, readPoolView } from './chain'
import { Legs, isUserRejection, minOutOf } from './plan'
import { BuiltTrade, RouteRefused, buildTrade, checkRoute, refusalText } from './route'

/** A stand-in wallet for previews (nobody connected yet). It only appears in the quote's route check, never in a transaction. */
export const PREVIEW_ACCOUNT: Address = '0x000000000000000000000000000000000000dEaD'

export function usePoolView(pool?: Address | null) {
  const client = usePublicClient({ chainId: V3_CHAIN_ID })
  return useSWR<PoolView, PoolError | Error>(pool ? `pool-view:${pool.toLowerCase()}` : null, () => readPoolView(client, pool as Address), {
    refreshInterval: 20_000,
    dedupingInterval: 5_000,
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  })
}

export interface Quote {
  amountOut: bigint
  minOut: bigint
  impact: Impact | null
  trade: BuiltTrade
  /** null when the built transaction passed the route check; otherwise the sentence to show. */
  routeError: string | null
  at: number
}

/** Fresh quote for one trade, refreshed while the amount is valid and the tab is visible. The route check runs on the bytes that would be signed. */
export function useTradeQuote(view: PoolView | undefined, legs: Legs | null, amountIn: bigint | null, user: Address | undefined) {
  const client = usePublicClient({ chainId: V3_CHAIN_ID })
  const [slippage] = useUserSlippage()
  const key = view && legs && amountIn ? `pool-quote:${view.facts.pool}:${legs.side}:${amountIn}:${user ?? 'none'}:${slippage}` : null
  return useSWR<Quote>(
    key,
    async () => {
      if (!view || !legs || !amountIn) throw new Error('no trade')
      const amountOut = await quoteTrade(client, view, legs.tokenIn.address, legs.tokenOut.address, amountIn)
      const minOut = minOutOf(amountOut, slippage)
      const recipient = user ?? PREVIEW_ACCOUNT
      const trade = buildTrade({
        dex: view.dex,
        facts: view.facts,
        tokenIn: legs.tokenIn.address,
        tokenOut: legs.tokenOut.address,
        amountIn,
        minOut,
        recipient,
        deadline: BigInt(Math.floor(Date.now() / 1000) + 1200),
        nativeIn: legs.nativeIn,
        nativeOut: legs.nativeOut,
      })
      let routeError: string | null = null
      try {
        await checkRoute({ dex: view.dex, facts: view.facts, expectedPool: view.facts.pool, user: recipient, trade, tokenIn: legs.tokenIn.address, tokenOut: legs.tokenOut.address, amountIn, resolvePool: makeResolver(client) })
      } catch (e) {
        routeError = refusalText(e)
      }
      return { amountOut, minOut, impact: impactOf(view, legs.tokenIn.address, amountIn, amountOut), trade, routeError, at: Date.now() }
    },
    { refreshInterval: 12_000, dedupingInterval: 3_000, revalidateOnFocus: true, keepPreviousData: false, shouldRetryOnError: false },
  )
}

/** Balance, allowance and BNB of the connected wallet for the token that goes in. */
export function useTradeAccount(user: Address | undefined, legs: Legs | null, spender: Address | undefined) {
  const client = usePublicClient({ chainId: V3_CHAIN_ID })
  const key = user && legs && spender ? `pool-account:${user}:${legs.tokenIn.address}:${spender}` : null
  return useSWR<Account>(key, () => readAccount(client, user as Address, legs!.tokenIn.address, spender as Address), {
    refreshInterval: 15_000,
    dedupingInterval: 3_000,
    revalidateOnFocus: true,
    shouldRetryOnError: false,
  })
}

export type TradeStatus =
  | { step: 'idle' }
  | { step: 'approving'; hash?: Hex }
  | { step: 'checking' }
  | { step: 'wallet' }
  | { step: 'pending'; hash: Hex }
  | { step: 'done'; hash: Hex; summary: string }
  | { step: 'failed'; message: string; hash?: Hex }

const rejected = isUserRejection

/**
 * Sends an approval or the swap from the connected wallet. The swap path is: rebuild with a fresh deadline and the current minimum, run the route
 * check again against the chain, simulate the exact bytes from the wallet (eth_call), and only then open the wallet. Any failure ends in `failed`
 * with nothing signed.
 */
export function useTradeSender() {
  const client = usePublicClient({ chainId: V3_CHAIN_ID })
  const { isConnected, wrongChain, switchToBsc } = useV3Wallet()
  const { toastSuccess, toastError } = useToast()
  const deadlineSetting = useTransactionDeadline()
  const [status, setStatus] = useState<TradeStatus>({ step: 'idle' })
  const busy = useRef(false)

  const reset = useCallback(() => setStatus({ step: 'idle' }), [])

  const wallet = useCallback(async () => {
    if (!isConnected) throw new Error('Connect a wallet first')
    if (wrongChain && !(await switchToBsc())) throw new Error('Switch your wallet to BNB Smart Chain to continue')
    const w = await getWalletClient({ chainId: V3_CHAIN_ID })
    if (!w) throw new Error('The wallet is not ready on BNB Smart Chain')
    return w
  }, [isConnected, wrongChain, switchToBsc])

  const approve = useCallback(
    async (token: Address, spender: Address, amount: bigint) => {
      if (busy.current) return false
      busy.current = true
      setStatus({ step: 'approving' })
      try {
        const w = await wallet()
        const hash = await w.writeContract({ address: token, abi: erc20Abi, functionName: 'approve', args: [spender, amount], ...(await liveGasParams(V3_CHAIN_ID)), chain: w.chain, account: w.account })
        setStatus({ step: 'approving', hash })
        const receipt = await client.waitForTransactionReceipt({ hash })
        if (receipt.status !== 'success') throw new Error('The approval transaction reverted')
        toastSuccess('Approval', hash)
        setStatus({ step: 'idle' })
        return true
      } catch (e) {
        const message = rejected(e) ? 'Cancelled in the wallet. Nothing was approved.' : String((e as { shortMessage?: string })?.shortMessage || (e as Error)?.message || e).slice(0, 220)
        toastError('Approval', message)
        setStatus({ step: 'failed', message })
        return false
      } finally {
        busy.current = false
      }
    },
    [wallet, client, toastSuccess, toastError],
  )

  const swap = useCallback(
    async (view: PoolView, legs: Legs, amountIn: bigint, amountOut: bigint, slippageBips: number, user: Address, expectedPool: Address) => {
      if (busy.current) return false
      busy.current = true
      setStatus({ step: 'checking' })
      try {
        const deadline = deadlineSetting ?? BigInt(Math.floor(Date.now() / 1000) + 1200)
        const minOut = minOutOf(amountOut, slippageBips)
        const trade = buildTrade({ dex: view.dex, facts: view.facts, tokenIn: legs.tokenIn.address, tokenOut: legs.tokenOut.address, amountIn, minOut, recipient: user, deadline, nativeIn: legs.nativeIn, nativeOut: legs.nativeOut })
        // the last word before the wallet: the bytes that will be signed are read back and the exchange's own factory names the pool
        await checkRoute({ dex: view.dex, facts: view.facts, expectedPool, user, trade, tokenIn: legs.tokenIn.address, tokenOut: legs.tokenOut.address, amountIn, resolvePool: makeResolver(client) })
        try {
          await client.call({ account: user, to: trade.to, data: trade.data, value: trade.value })
        } catch (e) {
          throw new Error(`The exchange would reject this trade right now (${String((e as { shortMessage?: string })?.shortMessage || (e as Error)?.message || e).slice(0, 160)}). Nothing was sent.`)
        }
        setStatus({ step: 'wallet' })
        const w = await wallet()
        const hash = await w.sendTransaction({ account: w.account, chain: w.chain, to: trade.to, data: trade.data, value: trade.value, ...(await liveGasParams(V3_CHAIN_ID)) })
        setStatus({ step: 'pending', hash })
        const receipt = await client.waitForTransactionReceipt({ hash })
        if (receipt.status !== 'success') throw Object.assign(new Error('The transaction reverted on chain'), { hash })
        const summary = `${legs.side === 'buy' ? 'Bought' : 'Sold'} through ${view.dex.name} · ${view.token0.symbol}/${view.token1.symbol}`
        toastSuccess(legs.side === 'buy' ? 'Bought' : 'Sold', hash)
        setStatus({ step: 'done', hash, summary })
        return true
      } catch (e) {
        const message = e instanceof RouteRefused ? refusalText(e) : rejected(e) ? 'Cancelled in the wallet. Nothing was sent.' : String((e as { shortMessage?: string })?.shortMessage || (e as Error)?.message || e).slice(0, 260)
        toastError('Trade', message)
        setStatus({ step: 'failed', message, hash: (e as { hash?: Hex })?.hash })
        return false
      } finally {
        busy.current = false
      }
    },
    [client, deadlineSetting, wallet, toastSuccess, toastError],
  )

  return useMemo(() => ({ status, approve, swap, reset, busy: status.step === 'approving' || status.step === 'checking' || status.step === 'wallet' || status.step === 'pending' }), [status, approve, swap, reset])
}
